import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { type Column, ListTable, type ListState } from '@/components/admin/ListTable';
import { MetaBox } from '@/components/admin/MetaBox';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import { EmptyState, ForbiddenState } from '@/components/admin/states';
import {
  fetchScoreDetail,
  fetchScorePeriods,
  fetchScoreRanking,
  type ScoreDetail,
  type ScoreRankingEntry,
} from '@/features/scores/api';
import { MEMBER_STATUS_LABELS, type MemberStatus } from '@/features/members/api';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { ApiError } from '@/lib/api/http';

/**
 * 採点結果（implementation-spec §10 / DECISIONS D-037）。
 * 順位一覧は受講生・事務局に公開。スコア内訳（components）は本人と事務局のみ。
 * 理事・サクセスは API 403・メニュー非表示（D-295。D-104 の開放を差し替え）。
 */
export function ScoreRankingPage(): ReactElement {
  const { user } = useAuth();
  const isStudent = user?.active_role === 'student';
  const isOffice = user?.active_role === 'office';

  const periodsQuery = useQuery({ queryKey: ['score-periods'], queryFn: fetchScorePeriods });
  const [period, setPeriod] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  useEffect(() => {
    if (!period && periodsQuery.data && periodsQuery.data.length > 0) {
      setPeriod(periodsQuery.data[0] as string);
    }
  }, [period, periodsQuery.data]);

  const rankingQuery = useQuery({
    queryKey: ['score-ranking', period],
    queryFn: () => fetchScoreRanking(period),
    enabled: Boolean(period),
  });

  const ownDetailQuery = useQuery({
    queryKey: ['score-detail', period, 'self'],
    queryFn: () => fetchScoreDetail(period),
    enabled: Boolean(period) && isStudent,
  });

  const selectedDetailQuery = useQuery({
    queryKey: ['score-detail', period, selectedMemberId],
    queryFn: () => fetchScoreDetail(period, selectedMemberId ?? undefined),
    enabled: Boolean(period) && isOffice && selectedMemberId !== null,
  });

  if (
    periodsQuery.isError &&
    periodsQuery.error instanceof ApiError &&
    periodsQuery.error.status === 403
  ) {
    return (
      <>
        <PageHeader title="採点結果" />
        <ForbiddenState />
      </>
    );
  }

  let rankingState: ListState = 'ready';
  if (rankingQuery.isPending) rankingState = 'loading';
  else if (rankingQuery.isError) {
    rankingState =
      rankingQuery.error instanceof ApiError && rankingQuery.error.status === 403
        ? 'forbidden'
        : 'error';
  }

  // モーダルの見出しに「誰の内訳か」を出す（読み込み中でも分かるように一覧側から取る）
  const selectedName =
    (rankingQuery.data ?? []).find((r) => r.member_id === selectedMemberId)?.member_name ?? null;

  // 氏名を主要列にする（順位を primary にすると、行アクションが順位セルに入って
  // 順位列だけ極端に広がり、氏名が遠く離れていた / D-094）
  const columns: Column<ScoreRankingEntry>[] = [
    {
      key: 'rank',
      header: '順位',
      className: 'w-16 whitespace-nowrap tabular-nums',
      render: (r) => `${r.rank}位`,
    },
    {
      key: 'member_name',
      header: '氏名',
      primary: true,
      // 当日その場で顔と名前を一致させられるよう、会員一覧と同じく写真つきで出す（D-112）
      render: (r) => (
        <span className="flex items-center gap-2">
          <PhotoPreview photoUrl={r.photo_url} name={r.member_name ?? '—'} size={32} />
          {r.member_name ?? '—'}
        </span>
      ),
    },
    {
      key: 'cohort',
      header: '期',
      className: 'whitespace-nowrap',
      render: (r) => r.cohort_name ?? '—',
    },
    {
      key: 'course',
      header: 'コース',
      className: 'whitespace-nowrap',
      render: (r) => r.course_name ?? '—',
    },
    {
      key: 'status',
      header: 'ステータス',
      className: 'whitespace-nowrap',
      render: (r) =>
        r.status !== null && r.status in MEMBER_STATUS_LABELS
          ? MEMBER_STATUS_LABELS[r.status as MemberStatus]
          : (r.status ?? '—'),
    },
    { key: 'specialty', header: '管掌領域', render: (r) => r.specialty ?? '—' },
    {
      key: 'score',
      header: '総合点',
      className: 'w-24 whitespace-nowrap text-right tabular-nums',
      render: (r) => r.score,
    },
    {
      // 前月比（D-110 A-1）。前月の公開が無い月・前月に載っていない人は「—」
      key: 'rank_delta',
      header: '前月比',
      className: 'w-20 whitespace-nowrap text-right tabular-nums',
      render: (r) => {
        if (r.rank_delta === null) return <span className="text-wptext-muted">—</span>;
        if (r.rank_delta > 0) {
          return <span className="font-semibold text-wpnotice-success">▲{r.rank_delta}</span>;
        }
        if (r.rank_delta < 0) {
          return <span className="text-wpnotice-error">▼{Math.abs(r.rank_delta)}</span>;
        }
        return <span className="text-wptext-muted">±0</span>;
      },
    },
  ];

  return (
    <>
      <PageHeader title="採点結果" description="順位は全受講生に公開されます。内訳は本人のみ閲覧できます。" />

      <div className="mb-4">
        <label className="block max-w-xs">
          <span className="mb-1.5 block text-wptext-muted">対象月</span>
          <select
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              setSelectedMemberId(null);
            }}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            {(periodsQuery.data ?? []).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!period ? (
        <EmptyState title="公開されている採点結果はまだありません" />
      ) : (
        <div className="flex flex-col gap-4">
          {isStudent && (
            <MetaBox title="自分のスコア内訳">
              {ownDetailQuery.isPending ? (
                <p className="text-wptext-muted">読み込み中…</p>
              ) : ownDetailQuery.isError ? (
                <p className="text-wptext-muted">この月のスコアはまだありません。</p>
              ) : (
                <ScoreDetailView detail={ownDetailQuery.data} showName />
              )}
            </MetaBox>
          )}

          <ListTable<ScoreRankingEntry>
            caption="順位一覧"
            rows={rankingQuery.data ?? []}
            columns={columns}
            rowKey={(r) => r.member_id}
            state={rankingState}
            onRetry={() => void rankingQuery.refetch()}
            emptyTitle="この月の順位はまだ公開されていません"
            rowActions={
              isOffice
                ? [
                    // 事務局にしか出ない行なので「（事務局のみ）」は不要
                    { label: '内訳を見る', onSelect: (r) => setSelectedMemberId(r.member_id) },
                  ]
                : undefined
            }
          />

          {/* 一覧の下に出すと、人数が多いとき結果が画面外になり
              「押しても何も起きない」ように見えるためモーダルにする（D-094） */}
          <Modal
            open={isOffice && selectedMemberId !== null}
            title={`スコア内訳: ${selectedName ?? ''}`}
            onClose={() => setSelectedMemberId(null)}
            footer={
              <button
                type="button"
                onClick={() => setSelectedMemberId(null)}
                className="btn-secondary"
              >
                閉じる
              </button>
            }
          >
            {selectedDetailQuery.isPending ? (
              <p className="text-wptext-muted">読み込み中…</p>
            ) : selectedDetailQuery.isError ? (
              <Notice kind="error" dismissible={false}>
                内訳の取得に失敗しました。
              </Notice>
            ) : (
              <ScoreDetailView detail={selectedDetailQuery.data} />
            )}
          </Modal>
        </div>
      )}
    </>
  );
}

/**
 * スコアの内訳。順位・総合点をまず大きく見せ、その下に項目別の点数を並べる。
 * 氏名はモーダルの見出し側に出るため、ここでは繰り返さない（D-094）。
 */
function ScoreDetailView({
  detail,
  showName = false,
}: {
  detail: ScoreDetail | undefined;
  /** 見出しに氏名が無い場所（受講生の自分の内訳）で使う */
  showName?: boolean;
}): ReactElement {
  if (!detail) return <p className="text-wptext-muted">—</p>;
  const componentEntries = Object.entries(detail.components);

  return (
    <div>
      {showName && <p className="mb-2 font-semibold text-wptext-strong">{detail.member_name ?? '—'}</p>}

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="flex-1 rounded-sm border border-wpborder bg-wpcanvas/50 px-3 py-2">
          <p className="text-xs text-wptext-muted">順位</p>
          <p className="text-2xl font-semibold tabular-nums text-wptext-strong">{detail.rank}位</p>
        </div>
        <div className="flex-1 rounded-sm border border-wpborder bg-wpcanvas/50 px-3 py-2">
          <p className="text-xs text-wptext-muted">総合点</p>
          <p className="text-2xl font-semibold tabular-nums text-wptext-strong">{detail.score}</p>
        </div>
      </div>

      <h3 className="mb-1 font-semibold text-wptext-strong">内訳</h3>
      {componentEntries.length === 0 ? (
        <p className="text-wptext-muted">内訳データはありません。</p>
      ) : (
        <table className="w-full text-left">
          <tbody>
            {componentEntries.map(([key, value]) => (
              <tr key={key} className="border-b border-wpborder last:border-b-0">
                <th scope="row" className="py-1.5 pr-3 font-normal text-wptext-muted">
                  {key}
                </th>
                <td className="py-1.5 text-right tabular-nums text-wptext-strong">
                  {String(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
